import type { RuleBoxHistoryEntry } from "@loot-os/api-contract";
import { dollars } from "@loot-os/api-contract/shopifyRuleBoxForm";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  ADDITION_REASON,
  actorLabel,
  HISTORY_TRIGGER,
  hasChangeDetail,
  historyBadge,
  historyHeadline,
  itemCountChange,
  priceChange,
  REMOVAL_REASON,
  versionChange,
} from "./historyLabels";
import { useNow } from "./hooks";
import { formatAgo, formatExact } from "./refreshTime";
import { useTRPC } from "./trpc";

const PAGE_SIZE = 20;

/** The products one refresh added, removed and revalued, each with its reason. */
function ChangeDetail({ changes }: { changes: RuleBoxHistoryEntry["changes"] }) {
  return (
    <s-box paddingInlineStart="base">
      <s-stack gap="base">
        {changes.added.length > 0 && (
          <s-stack gap="small-200">
            <s-heading>Added</s-heading>
            {changes.added.map((item) => (
              <s-text key={item.catalogItemId}>
                {item.name} ({dollars(item.valueCents)}) — {ADDITION_REASON[item.reason]}
              </s-text>
            ))}
          </s-stack>
        )}
        {changes.removed.length > 0 && (
          <s-stack gap="small-200">
            <s-heading>Removed</s-heading>
            {changes.removed.map((item) => (
              <s-text key={item.catalogItemId}>
                {item.name} ({dollars(item.valueCents)}) — {REMOVAL_REASON[item.reason]}
              </s-text>
            ))}
          </s-stack>
        )}
        {changes.revalued.length > 0 && (
          <s-stack gap="small-200">
            <s-heading>Value changed</s-heading>
            {changes.revalued.map((item) => (
              <s-text key={item.catalogItemId}>
                {item.name}: {dollars(item.fromCents)} → {dollars(item.toCents)}
              </s-text>
            ))}
          </s-stack>
        )}
      </s-stack>
    </s-box>
  );
}

/** One refresh: when, what happened in a sentence, who set it off, and what moved. */
function HistoryRow({ entry, now }: { entry: RuleBoxHistoryEntry; now: Date }) {
  const [open, setOpen] = useState(false);
  const at = new Date(entry.at);
  const outcome = historyBadge(entry);
  const facts = [versionChange(entry), priceChange(entry), itemCountChange(entry)].filter(
    (fact): fact is string => fact !== null,
  );
  return (
    <s-stack gap="small-200">
      <s-stack direction="inline" gap="small-200" alignItems="center">
        <s-badge tone={outcome.tone}>{outcome.label}</s-badge>
        <s-text type="strong">{historyHeadline(entry)}</s-text>
      </s-stack>
      <s-text color="subdued">
        <span title={formatExact(at)}>{formatAgo(at, now)}</span>
        {` · ${actorLabel(entry.actor)} · ${HISTORY_TRIGGER[entry.trigger]}`}
        {facts.length > 0 && ` · ${facts.join(" · ")}`}
      </s-text>
      {entry.outcome === "refused" && (
        <s-text color="subdued">The box kept selling its last version.</s-text>
      )}
      {hasChangeDetail(entry) && (
        <>
          <s-stack direction="inline">
            <s-button
              variant="tertiary"
              icon={open ? "chevron-up" : "chevron-down"}
              onClick={() => setOpen(!open)}
            >
              {open ? "Hide products" : "Show products"}
            </s-button>
          </s-stack>
          {open && <ChangeDetail changes={entry.changes} />}
        </>
      )}
    </s-stack>
  );
}

/**
 * A rule box's history, newest first: every rebuild from the daily refresh, a price or stock
 * change in the store, "Refresh now" or a rules edit, and what it changed. "Load older" pages back.
 */
export function RuleBoxHistory({ boxId }: { boxId: string }) {
  const trpc = useTRPC();
  const now = useNow(30_000);
  const history = useInfiniteQuery(
    trpc.boxes.history.infiniteQueryOptions(
      { boxId, limit: PAGE_SIZE },
      { getNextPageParam: (last) => last.nextCursor },
    ),
  );
  const entries = history.data?.pages.flatMap((page) => page.entries) ?? [];

  if (history.isLoading) return <s-paragraph>Loading the box's history…</s-paragraph>;
  if (history.isError && entries.length === 0) {
    return (
      <s-stack gap="base">
        <s-banner tone="critical">This box's history could not be loaded.</s-banner>
        <s-stack direction="inline">
          <s-button onClick={() => void history.refetch()}>Try again</s-button>
        </s-stack>
      </s-stack>
    );
  }
  if (entries.length === 0) {
    return (
      <s-paragraph color="subdued">
        No history yet. Every time the box is rebuilt (daily, when a product's price or stock
        changes, or when you edit it) it's listed here.
      </s-paragraph>
    );
  }

  return (
    <s-stack gap="base">
      {entries.map((entry, index) => (
        <s-stack key={entry.id} gap="base">
          {index > 0 && <s-divider />}
          <HistoryRow entry={entry} now={now} />
        </s-stack>
      ))}
      {history.isFetchNextPageError && (
        <s-banner tone="critical">Older history could not be loaded. Try again.</s-banner>
      )}
      {history.hasNextPage && (
        <s-stack direction="inline">
          <s-button
            loading={history.isFetchingNextPage || undefined}
            onClick={() => void history.fetchNextPage()}
          >
            Load older
          </s-button>
        </s-stack>
      )}
    </s-stack>
  );
}
